import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  catchError,
  filter,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from '../../../core/services/auth.service';
import { FileUploadService } from '../../../core/services/file-upload.service';
import { LoggingService } from '../../../core/services/logging.service';
import { DialogService } from '../../../core/services/dialog.service';
import { BaseRepository } from '../../../infrastructure/http/repositories/base.repository';
import { CreateRequestWizardActions } from '../../../store/requests/requests.actions';
import {
  selectCreateRequestLoading,
  selectCreateRequestError,
  selectCreatedRequestId,
} from '../../../store/requests/requests.selectors';
import envConfig from '../../../env-config';
import {
  WIZARD_STEPS,
  WizardStep,
  TOTAL_STEPS,
  Step1FormData,
} from './create-request-wizard.types';
import { StepUploadFilesComponent } from './step-upload-files/step-upload-files.component';
import { StepExpediteRequestComponent } from './step-expedite-request/step-expedite-request.component';
import { StepSummaryComponent } from './step-summary/step-summary.component';
import { StepConfirmationComponent } from './step-confirmation/step-confirmation.component';

@Component({
  selector: 'app-create-request-wizard',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    ReactiveFormsModule,
    StepUploadFilesComponent,
    StepExpediteRequestComponent,
    StepSummaryComponent,
    StepConfirmationComponent,
  ],
  templateUrl: './create-request-wizard.component.html',
  styleUrls: ['./create-request-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateRequestWizardComponent
  extends BaseRepository
  implements OnInit, OnDestroy
{
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggingService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly bffUrl = envConfig.bffUrl ?? '';

  private currentUserEmail = '';

  companyLookupError: string | null = null;
  isLookingUpCompany = false;
  isSubmitting = false;
  submitError: string | null = null;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<void>();

  @ViewChild(StepUploadFilesComponent)
  uploadFilesStep?: StepUploadFilesComponent;
  @ViewChild(StepExpediteRequestComponent)
  expediteStep?: StepExpediteRequestComponent;
  @ViewChild(StepSummaryComponent) summaryStep?: StepSummaryComponent;
  @ViewChild(StepConfirmationComponent)
  confirmationStep?: StepConfirmationComponent;

  readonly steps: WizardStep[] = WIZARD_STEPS;
  readonly totalSteps = TOTAL_STEPS;
  currentStep = 1;
  isDirty = false;
  hasUploadedFiles = false;
  submittedRequestNumber: string | null = null;
  private savedIsExpedited = false;

  /** Ties all uploaded files to this wizard session in Swift temp storage */
  private readonly wizardRequestGuid = uuidv4().toUpperCase();

  step1Form!: FormGroup;

  ngOnInit(): void {
    this.initializeStep1Form();
    this.loadUserData();
    this.subscribeToWizardState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeStep1Form(): void {
    this.step1Form = this.fb.group({
      companyName: ['', Validators.required],
      arNumber: [{ value: '', disabled: true }, Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^[\d\s\-+()]+$/)],
      ],
      email: ['', Validators.required],
    });

    // Watch Company Name changes and search for companies
    this.step1Form
      .get('companyName')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        if (searchTerm?.trim()) {
          this.searchCompanies(searchTerm.trim());
        } else {
          this.step1Form.patchValue(
            {
              arNumber: '',
              firstName: '',
              lastName: '',
              phoneNumber: '',
              email: '',
            },
            { emitEvent: false }
          );
          this.companyLookupError = null;
        }
      });

    this.step1Form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isDirty = true;
    });
  }

  private loadUserData(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          this.currentUserEmail = user.email ?? '';
          this.step1Form.patchValue(
            {
              email: user.email ?? '',
              phoneNumber: user.workPhoneNumber ?? '',
              firstName: user.firstName ?? '',
              lastName: user.lastName ?? '',
            },
            { emitEvent: false }
          );

          this.isDirty = false;
        }
      });
  }

  companySearchResults: SalesforceAccount[] = [];

  /**
   * Search for companies by name or AR number
   */
  private searchCompanies(searchTerm: string): void {
    this.isLookingUpCompany = true;
    this.companyLookupError = null;
    this.cdr.markForCheck();

    this.logger.debug('CreateRequestWizard: Searching for companies', {
      searchTerm,
    });

    this.http
      .get<SalesforceSearchResponse>(
        `${this.bffUrl}/api/v1/salesforce/accounts/search?arNumber=${searchTerm}`,
        { context: this.authContext() }
      )
      .pipe(
        catchError(error => {
          this.logger.warn('CreateRequestWizard: Company search failed', {
            searchTerm,
            error,
          });
          return of(null);
        })
      )
      .subscribe(response => {
        this.isLookingUpCompany = false;

        if (response?.data && response.data.length > 0) {
          this.companySearchResults = response.data;
          this.logger.debug('CreateRequestWizard: Companies found', {
            searchTerm,
            count: response.data.length,
          });
          this.companyLookupError = null;
        } else {
          this.companySearchResults = [];
          this.companyLookupError = `No companies found matching: ${searchTerm}`;
        }

        this.cdr.markForCheck();
      });
  }

  /**
   * Select a company from search results and populate details
   */
  selectCompany(company: SalesforceAccount): void {
    this.logger.debug('CreateRequestWizard: Company selected', {
      arNumber: company.arNumber,
      companyName: company.companyName,
    });

    this.step1Form.patchValue(
      {
        companyName: company.companyName,
        arNumber: company.arNumber,
      },
      { emitEvent: false }
    );

    this.companySearchResults = [];
    this.companyLookupError = null;
  }

  onNext(): void {
    if (this.currentStep === 1) {
      this.advanceFromStep1();
    } else if (this.currentStep === 2) {
      this.advanceFromStep2();
    } else if (this.currentStep === 3) {
      this.advanceFromStep3();
    } else if (this.currentStep === 4) {
      this.advanceFromStep4();
    }
  }

  arNumberMissing = false;

  private advanceFromStep1(): void {
    const arNumber = this.step1Form.getRawValue().arNumber as string;
    this.arNumberMissing = !arNumber?.trim();

    if (this.arNumberMissing || !this.step1Form.valid) {
      this.step1Form.markAllAsTouched();
      return;
    }

    this.fileUploadService.setRequestContext(arNumber, this.wizardRequestGuid);
    this.currentStep++;
  }

  private advanceFromStep2(): void {
    if (this.uploadFilesStep?.canProceed()) {
      this.currentStep++;
    } else if (this.uploadFilesStep?.isUploading) {
      this.dialogService
        .alert(
          'Please wait for uploads to complete before proceeding.',
          'Upload In Progress'
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  private advanceFromStep3(): void {
    if (this.expediteStep?.canProceed()) {
      this.savedIsExpedited = this.expediteStep.isExpedited();
      this.currentStep++;
    }
  }

  private advanceFromStep4(): void {
    if (this.summaryStep?.canProceed() && !this.isSubmitting) {
      this.submitRequest();
    }
  }

  onBack(): void {
    if (this.currentStep > 1) {
      if (this.currentStep === 2 && this.uploadFilesStep?.shouldWarnOnLeave()) {
        this.dialogService
          .confirm(
            this.uploadFilesStep.getLeaveWarningMessage(),
            'Confirm Navigation'
          )
          .subscribe(confirmed => {
            if (confirmed) {
              this.currentStep--;
            }
          });
      } else {
        this.currentStep--;
      }
    }
  }

  onCancel(): void {
    this.fileUploadService.cancelSession();
    this.close.emit();
  }

  setFilesUploaded(hasFiles: boolean): void {
    this.hasUploadedFiles = hasFiles;
  }

  isStepCompleted(stepNumber: number): boolean {
    return stepNumber < this.currentStep;
  }

  isStepCurrent(stepNumber: number): boolean {
    return stepNumber === this.currentStep;
  }

  get isFirstStep(): boolean {
    return this.currentStep === 1;
  }

  get isLastStep(): boolean {
    return this.currentStep === this.totalSteps;
  }

  /**
   * Returns the expedite selection from Step 3.
   * Used by Summary step to display the user's choice.
   */
  get isExpedited(): boolean {
    return this.expediteStep?.isExpedited() ?? this.savedIsExpedited;
  }

  /**
   * Returns contact data from Step 1 form.
   * Used by Summary step to display the user's contact information.
   */
  get contactData(): Step1FormData {
    return this.step1Form.getRawValue() as Step1FormData;
  }

  /**
   * Returns parsed files from the FileUploadService.
   * Used by Summary step to display the files grid.
   */
  get parsedFiles() {
    return this.fileUploadService.parsedFiles;
  }

  /**
   * Returns file totals from the FileUploadService.
   * Used by Summary step to display footer totals.
   */
  get fileTotals() {
    return this.fileUploadService.totals;
  }

  getFieldError(fieldName: string): string | null {
    const field = this.step1Form.get(fieldName);
    if (field?.invalid && field?.touched) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors?.['pattern']) {
        return 'Valid phone number is required';
      }
    }
    return null;
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      arNumber: 'AR Number',
      companyName: 'Company',
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email Address',
    };
    return labels[fieldName] || fieldName;
  }

  private clearWizard(): void {
    this.store.dispatch(CreateRequestWizardActions.clearWizard());
    this.close.emit();
  }

  /**
   * Navigate to the newly created Request Details page from the Confirmation step.
   */
  onViewRequest(): void {
    if (!this.submittedRequestNumber) {
      return;
    }

    const requestNumber = this.submittedRequestNumber;

    this.clearWizard();
    this.router.navigate(['/requestDetails', requestNumber]);
  }

  /**
   * Navigate back to the Requests list page from the Confirmation step.
   */
  onBackToRequests(): void {
    this.clearWizard();
    this.router.navigate(['/requests']);
  }

  /**
   * Subscribe to wizard submission state from the NgRx store.
   * Advances to step 5 on success; surfaces errors on failure.
   */
  private subscribeToWizardState(): void {
    this.store
      .select(selectCreateRequestLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isSubmitting = loading;
        this.cdr.markForCheck();
      });

    this.store
      .select(selectCreateRequestError)
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.submitError = error;
        this.cdr.markForCheck();
      });

    this.store
      .select(selectCreatedRequestId)
      .pipe(
        filter(id => id !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(requestId => {
        this.submittedRequestNumber = String(requestId);
        this.currentStep = 5;
        this.cdr.markForCheck();
      });
  }

  /**
   * Dispatch the submit action with serializable wizard data.
   * The effect reads File objects directly from FileUploadService.
   */
  private submitRequest(): void {
    const step1 = this.step1Form.getRawValue() as Step1FormData;
    const parsedFiles = this.fileUploadService.parsedFiles;
    const sla = this.savedIsExpedited;
    const isSlaFeeWaived = parsedFiles.some(f => f.isSlaFeeWaived);

    this.submitError = null;

    this.store.dispatch(
      CreateRequestWizardActions.submitRequest({
        arNumber: step1.arNumber,
        userEmail: this.currentUserEmail,
        contactName: `${step1.firstName} ${step1.lastName}`.trim(),
        contactEmails: step1.email,
        contactPhone: step1.phoneNumber,
        isExpedited: sla,
        isSlaFeeWaived,
        requestGuid: this.wizardRequestGuid,
      })
    );
  }
}

/**
 * Salesforce account response from search API
 */
interface SalesforceAccount {
  arNumber: string;
  companyName: string;
  arStatus: string;
  phone: string;
  customerType: string;
  subprocessorConsent: string;
}

/**
 * Salesforce search API response
 */
interface SalesforceSearchResponse {
  data: SalesforceAccount[];
  statusCode: number;
  message: string;
  isSuccess: boolean;
  errorType: string | null;
}
